import { Component, OnInit } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import {
  PTSDSimulationResults,
  SimulationResponse,
  SimulationResults,
} from '../../../core/models/simulation.model';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import { ActivatedRoute } from '@angular/router';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/accessibility';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import { map, merge } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { SimulationDirectionsTableComponent } from '../../simulation-directions-table/simulation-directions-table.component';
import { 
  createBaseLogChartOptions, 
  createLineSeries 
} from '../../../utils/chart/chart-config.util';
import { formatValue } from '../../../utils/format.util';

type PtsdChartType = 'n_tsd' | 'cum_tsd';

@Component({
  selector: 'app-ptsd-simulation-results',
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatExpansionModule,
    HighchartsChartModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatTableModule,
    SimulationOverviewComponent,
    SimulationDirectionsTableComponent
],
  templateUrl: './ptsd-simulation-results.component.html',
  styleUrl: './ptsd-simulation-results.component.scss',
  standalone: true,
})
export class PtsdSimulationResultsComponent extends Destroyable(Object) implements OnInit {
  readonly Highcharts: typeof Highcharts = Highcharts;
  readonly componentId: string = 'ptsd-simulation-results';
  simulationResponse: SimulationResponse<PTSDSimulationResults> | null = null;
  directionsForm!: FormGroup;
  flipDirectionsForm!: FormGroup;
  nTsdChart?: Highcharts.Chart;
  cumTsdChart?: Highcharts.Chart;
  nTsdChartOptions!: Highcharts.Options;
  cumTsdChartOptions!: Highcharts.Options;
  isLoading: boolean = false;
  displayedColumns: string[] = [];
  tableData: any[] = [];

  get results(): PTSDSimulationResults[] | undefined {
    return this.simulationResponse?.results;
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly fb: FormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.initDirectionForms();

    const simulationId: string | null = this.route.snapshot.paramMap.get('id');
    if (simulationId) {
      this.loadSimulation(simulationId);
    }
  }

  saveNTsdChartInstance(chart: Highcharts.Chart): void {
    this.nTsdChart = chart;
    // Populate both charts after they're initialized
    if (this.nTsdChart && this.cumTsdChart) {
      this.initializeChartSeries();
    }
  }

  saveCumTsdChartInstance(chart: Highcharts.Chart): void {
    this.cumTsdChart = chart;
    // Populate both charts after they're initialized
    if (this.nTsdChart && this.cumTsdChart) {
      this.initializeChartSeries();
    }
  }

  private initializeChartSeries(): void {
    if (!this.nTsdChart || !this.cumTsdChart) return;

    const directionOrder: string[] = ['x', 'y', 'z'];
    const flipOrder: boolean[] = [false, true];

    // Add all N-TSD series to nTsdChart
    directionOrder.forEach((dir) => {
      const directionResult: PTSDSimulationResults | undefined = this.getResultByDirection(dir, true);
      if (directionResult) {
        flipOrder.forEach((flip) => {
          const series = this.createChartSeriesByFlipType(directionResult, 'n_tsd', flip);
          this.nTsdChart!.addSeries(series, false);
        });
      }
    });

    // Add all Cum-TSD series to cumTsdChart
    directionOrder.forEach((dir) => {
      const directionResult: PTSDSimulationResults | undefined = this.getResultByDirection(dir, true);
      if (directionResult) {
        flipOrder.forEach((flip) => {
          const series = this.createChartSeriesByFlipType(directionResult, 'cum_tsd', flip);
          this.cumTsdChart!.addSeries(series, false);
        });
      }
    });

    // Redraw both charts
    this.nTsdChart.redraw();
    this.cumTsdChart.redraw();

    // Apply initial visibility based on form state
    this.updateChartsSeriesVisibility();
  }

  getRadiusValues(result: PTSDSimulationResults): string[] {
    return result?.result?.radiusM ?? [];
  }

  getNTsdValues(result: PTSDSimulationResults): string[] {
    return result?.result?.nTsd ?? [];
  }

  getCumTsdValues(result: PTSDSimulationResults): string[] {
    return result?.result?.cumTsd ?? [];
  }

  getRadiusFlipValues(result: PTSDSimulationResults): string[] {
    return result?.result?.radiusMFlip ?? [];
  }

  getNTsdFlipValues(result: PTSDSimulationResults): string[] {
    return result?.result?.nTsdFlip ?? [];
  }

  getCumTsdFlipValues(result: PTSDSimulationResults): string[] {
    return result?.result?.cumTsdFlip ?? [];
  }

  getDirectionControl(direction: string): FormControl {
    return this.directionsForm.get(direction) as FormControl;
  }

  getFlipDirectionControl(direction: string): FormControl {
    return this.flipDirectionsForm.get(direction) as FormControl;
  }

  getResultByDirection(direction: string, checkStatus: boolean): PTSDSimulationResults | undefined {
    return this.results?.find((result: PTSDSimulationResults) => {
      return (!checkStatus || this.isResultValid(result)) &&
        result.direction.toLowerCase() === direction.toLowerCase();
    });
  }

  isPtsdCurveDataValid(): boolean {
    return !!this.results &&
      this.results.length > 0;
  }

  isResultValid(result: PTSDSimulationResults): boolean {
    return result.status?.toLowerCase() === 'success' &&
      Object.values(result.result ?? {}).every((list: string[]) => list?.length > 0);
  }

  allResultsInvalid(): boolean {
    return !!this.results?.length &&
      this.results.every((result: PTSDSimulationResults) => !this.isResultValid(result));
  }

  getColumnStyle(columnName: string): Record<string, string> {
    return {
      'background': this.defineColorForTable(columnName),
      'width': `${100 / (this.displayedColumns.length || 1)}%`
    }
  }

  downloadResults(): void {
    if (this.simulationResponse && this.checkIfAtLeastOneDirectionSelected()) {
      const directions: Record<string, boolean> = this.getSelectedDirections();
      this.simulationsService.downloadResult(this.simulationResponse.id, directions);
    }
  }

  private loadSimulation(id: string): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(id, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((simulation: SimulationResponse<SimulationResults>) => {
        this.simulationResponse = simulation as SimulationResponse<PTSDSimulationResults>;

        if (this.isPtsdCurveDataValid() && !this.allResultsInvalid()) {
          this.configureDirectionControls();
          this.initCharts();
          this.updateTable();
        }
      });
  }

  private configureDirectionControls(): void {
    ['x', 'y', 'z'].forEach((direction: string) => {
      const hasValidResultForDirection: boolean = this.results!
        .some((result: PTSDSimulationResults) => {
          return this.isResultValid(result) &&
            result?.direction?.toLowerCase() === direction;
        });

      if (hasValidResultForDirection) {
        this.getDirectionControl(direction).enable({ emitEvent: false });
        this.getDirectionControl(direction).patchValue(true, { emitEvent: false });
        this.getFlipDirectionControl(direction).enable({ emitEvent: false });
        this.getFlipDirectionControl(direction).patchValue(true, { emitEvent: false });
      } else {
        this.getDirectionControl(direction).patchValue(false, { emitEvent: false });
        this.getDirectionControl(direction).disable({ emitEvent: false });
        this.getFlipDirectionControl(direction).patchValue(false, { emitEvent: false });
        this.getFlipDirectionControl(direction).disable({ emitEvent: false });
      }
    });
  }

  private initCharts(): void {
    this.nTsdChartOptions = this.createLinearChart(
      'Normalized Throat Size Distribution by radius',
      'Pore throat radius size, microns',
      'Pore Volume (fraction)',
      'n_tsd',
    );
    this.cumTsdChartOptions = this.createLinearChart(
      'Cumulative Throat Size Distribution by radius',
      'Pore throat radius size, microns',
      'Pore Volume (cumulative)',
      'cum_tsd',
    );
  }

  private createLinearChart(
    title: string,
    xAxisTitle: string,
    yAxisTitle: string,
    chartType: PtsdChartType,
  ): Highcharts.Options {
    // Get min X value from all valid results for proper axis scaling
    const minX: number = Math.min(
      ...this.results!
        .filter((directionResult: PTSDSimulationResults) => this.isResultValid(directionResult))
        .map((directionResult: PTSDSimulationResults): number => {
          return Math.min(+directionResult?.result?.radiusM?.[0], +directionResult?.result?.radiusMFlip?.[0]);
        })
    );

    return createBaseLogChartOptions({
      title,
      xAxisTitle,
      yAxisTitle,
      series: [], // Start with empty series, will be populated after chart initialization
      xAxisType: 'logarithmic',
      yAxisType: 'linear',
      xMin: minX >= 1 ? 1 : 0.1,
      xTickInterval: 1,
      xMinorTickInterval: 0.1,
      yMin: 0,
      yMax: 1,
      tooltipFormatter: function() {
        return `<b>${this.series.name}</b><br/><b>Radius:</b> ${this.x?.toFixed(2)} мicrons<br/><b>Pore volume fraction:</b> ${this.y?.toFixed(2)}`;
      },
    });
  }

  private updateSeriesForCharts(direction: string, flip: boolean, checked: boolean): void {
    // Update series visibility without removing/re-adding them
    this.updateChartsSeriesVisibility();
  }

  private updateChartsSeriesVisibility(): void {
    if (!this.nTsdChart || !this.cumTsdChart) return;

    const directionOrder: string[] = ['x', 'y', 'z'];
    const flipOrder: boolean[] = [false, true];

    // Determine which series should be visible based on current selection state
    const selectedSeriesNames = new Set<string>();
    directionOrder.forEach((dir) => {
      const directionResult: PTSDSimulationResults | undefined = this.getResultByDirection(dir, true);
      if (directionResult) {
        flipOrder.forEach((flip) => {
          if (flip ? this.getFlipDirectionControl(dir).value : this.getDirectionControl(dir).value) {
            const seriesName = this.createChartSeriesByFlipType(
              directionResult,
              'n_tsd',
              flip,
            ).name;
            selectedSeriesNames.add(seriesName as string);
          }
        });
      }
    });

    // Update series visibility in both charts: hide both line and legend entry
    [this.nTsdChart, this.cumTsdChart].forEach((chart) => {
      chart.series.forEach((series) => {
        const isSelected = selectedSeriesNames.has(series.name ?? '');
        series.setVisible(isSelected, false);
        // Also hide from legend when not selected
        series.update({ showInLegend: isSelected } as Highcharts.SeriesOptionsType, false);
      });
      chart.redraw();
    });
  }

  private initChartSeries(result: PTSDSimulationResults, chartType: PtsdChartType): Highcharts.SeriesOptionsType[] | null {
    if (!this.isResultValid(result)) return null;

    // Generate all possible series for this direction (normal and flip)
    const directionSeries: Highcharts.SeriesOptionsType[] = [];

    // Add normal series
    const normalSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByFlipType(result, chartType, false);
    directionSeries.push(normalSeries);

    // Add flip series
    const flipSeries: Highcharts.SeriesOptionsType = this.createChartSeriesByFlipType(result, chartType, true);
    directionSeries.push(flipSeries);

    return directionSeries;
  }

  private createChartSeriesByFlipType(result: PTSDSimulationResults, chartType: PtsdChartType, flip: boolean): Highcharts.SeriesOptionsType {
    let data: number[][];
    let name: string;

    if (!flip) {
      name = result.direction.toUpperCase();
      data = this.getRadiusValues(result).map((radius: string, index: number) => {
        const yValues: string[] = chartType === 'n_tsd'
          ? this.getNTsdValues(result)
          : this.getCumTsdValues(result);

        return [Number(radius), Number(yValues[index])];
      });
    } else {
      name = `Flip ${result.direction.toUpperCase()}`;
      data = this.getRadiusFlipValues(result).map((radius: string, index: number) => {
        const yValues: string[] = chartType === 'n_tsd'
          ? this.getNTsdFlipValues(result)
          : this.getCumTsdFlipValues(result);

        return [Number(radius), Number(yValues[index])];
      });
    }

    return createLineSeries({
      name,
      data,
      color: this.defineColorForChartLine(result.direction, flip),
      symbol: 'circle',
      markerEnabled: true,
      custom: {
        direction: result.direction.toLowerCase(),
        flip,
      },
    });
  }

  private updateTable(): void {
    const rows: any[] = [];
    const maxLength: number = Math.max(
      ...this.results!
        .filter((directionResult: PTSDSimulationResults) => this.isResultValid(directionResult))
        .map((directionResult: PTSDSimulationResults) => {
          return Math.max(directionResult?.result?.radiusM?.length, directionResult?.result?.radiusMFlip?.length);
        }),
    );

    for (let i = 0; i < maxLength; i++) {
      const row: any = {};

      this.results!
        .filter((result: PTSDSimulationResults) => this.isResultValid(result))
        .forEach((result: PTSDSimulationResults) => {
          const direction: string = result.direction.toUpperCase();

          const isNormalEnabled: boolean = this.getDirectionControl(result.direction)?.value;
          const isFlipEnabled: boolean = this.getFlipDirectionControl(result.direction)?.value;

          if (isNormalEnabled) {
            row[`Radius ${direction}`] = formatValue(this.getRadiusValues(result)[i]);
            row[`Norm ${direction}`] = formatValue(this.getNTsdValues(result)[i]);
            row[`Cum ${direction}`] = formatValue(this.getCumTsdValues(result)[i]);
          }
          if (isFlipEnabled) {
            row[`Radius flip ${direction}`] = formatValue(this.getRadiusFlipValues(result)[i]);
            row[`Norm flip ${direction}`] = formatValue(this.getNTsdFlipValues(result)[i]);
            row[`Cum flip ${direction}`] = formatValue(this.getCumTsdFlipValues(result)[i]);
          }
        });

      rows.push(row);
    }

    this.displayedColumns = rows.length ? Object.keys(rows[0]) : [];
    this.tableData = rows;
  }

  private defineColorForChartLine(direction: string, flip: boolean): string {
    switch (direction) {
      case 'x':
        return flip ? '#0066CC' : '#003D99';
      case 'y':
        return flip ? '#2D7A3E' : '#1F4620';
      case 'z':
        return flip ? '#FF8C00' : '#CC5200';
      default:
        return '#4A4A4A';
    }
  }

  private defineColorForTable(columnName: string): string {
    switch (columnName) {
      case 'Radius X':
      case 'Norm X':
      case 'Cum X':
        return '#003D99';
      case 'Radius flip X':
      case 'Norm flip X':
      case 'Cum flip X':
        return '#0066CC';
      case 'Radius Y':
      case 'Norm Y':
      case 'Cum Y':
        return '#1F4620';
      case 'Radius flip Y':
      case 'Norm flip Y':
      case 'Cum flip Y':
        return '#2D7A3E';
      case 'Radius Z':
      case 'Norm Z':
      case 'Cum Z':
        return '#CC5200';
      case 'Radius flip Z':
      case 'Norm flip Z':
      case 'Cum flip Z':
        return '#FF8C00';
      default:
        return '#4A4A4A';
    }
  }

  private initDirectionForms(): void {
    this.directionsForm = this.fb.group({
      x: [{ value: false, disabled: true }],
      y: [{ value: false, disabled: true }],
      z: [{ value: false, disabled: true }],
    });
    this.flipDirectionsForm = this.fb.group({
      x: [{ value: false, disabled: true }],
      y: [{ value: false, disabled: true }],
      z: [{ value: false, disabled: true }],
    });

    merge(
      this.getDirectionControl('x').valueChanges.pipe(map(value => ({ direction: 'x', flip: false, value }))),
      this.getDirectionControl('y').valueChanges.pipe(map(value => ({ direction: 'y', flip: false, value }))),
      this.getDirectionControl('z').valueChanges.pipe(map(value => ({ direction: 'z', flip: false, value }))),
      this.getFlipDirectionControl('x').valueChanges.pipe(map(value => ({ direction: 'x', flip: true, value }))),
      this.getFlipDirectionControl('y').valueChanges.pipe(map(value => ({ direction: 'y', flip: true, value }))),
      this.getFlipDirectionControl('z').valueChanges.pipe(map(value => ({ direction: 'z', flip: true, value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ direction, flip, value }) => {
        this.updateSeriesForCharts(direction, flip, value);
        this.updateTable();
      });
  }

  private getSelectedDirections(): Record<string, boolean> {
    return {
      directionX: this.getDirectionControl('x').value || this.getFlipDirectionControl('x').value,
      directionY: this.getDirectionControl('y').value || this.getFlipDirectionControl('y').value,
      directionZ: this.getDirectionControl('z').value || this.getFlipDirectionControl('z').value,
    };
  }

  private checkIfAtLeastOneDirectionSelected(): boolean {
    const selectedDirections: Record<string, boolean> = this.getSelectedDirections();
    return Object.values(selectedDirections).some(Boolean);
  }

  hasDirectionData(direction: string): boolean {
    return this.getDirectionControl(direction.toLowerCase())?.value ?? false;
  }

  hasFlipDirectionData(direction: string): boolean {
    return this.getFlipDirectionControl(direction.toLowerCase())?.value ?? false;
  }

  getDirectionColor(direction: string): string {
    switch (direction.toLowerCase()) {
      case 'x':
        return '#003D99';
      case 'y':
        return '#1F4620';
      case 'z':
        return '#CC5200';
      default:
        return '#4A4A4A';
    }
  }

  getFlipDirectionColor(direction: string): string {
    switch (direction.toLowerCase()) {
      case 'x':
        return '#0066CC';
      case 'y':
        return '#2D7A3E';
      case 'z':
        return '#FF8C00';
      default:
        return '#4A4A4A';
    }
  }

  getHeaderColor(column: string): string {
    // Extract direction from column name (e.g., "Radius X" -> "X")
    const parts = column.split(' ');
    const directionPart = parts[parts.length - 1]; // Last part is the direction

    if (column.includes('flip')) {
      return this.getFlipDirectionColor(directionPart);
    } else {
      return this.getDirectionColor(directionPart);
    }
  }

  getHeaderGroupName(column: string): string {
    // Extract direction from column name
    const parts = column.split(' ');
    const directionPart = parts[parts.length - 1]; // Last part is the direction

    if (column.includes('flip')) {
      return `${directionPart} Flip`;
    } else {
      return directionPart;
    }
  }

  getSubHeaderName(column: string): string {
    // Extract sub-header name (Radius, Norm, or Cum) with units
    if (column.includes('Radius')) {
      return 'Radius (microns)';
    } else if (column.includes('Norm')) {
      return 'Pore Volume (fraction)';
    } else if (column.includes('Cum')) {
      return 'Pore Volume (cumulative)';
    }
    return '';
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });
  }
}
